import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for smooth character-by-character text streaming
 * Decouples server chunk arrival from visual rendering speed
 * Similar to Google Gemini's streaming effect
 *
 * @param {string} incomingText - The full text received so far
 * @param {number} charsPerFrame - Characters to reveal per animation frame (default: 8)
 * @returns {{ displayedText: string, isAnimating: boolean, skipToEnd: () => void }}
 */
export function useStreamingText(incomingText, charsPerFrame = 8) {
  const [displayedText, setDisplayedText] = useState('')
  const [isAnimating, setIsAnimating] = useState(false)
  const incomingTextRef = useRef('')
  const animationFrameRef = useRef(null)
  const displayIndexRef = useRef(0)
  const lastRenderTimeRef = useRef(0)
  const skipRequestedRef = useRef(false)
  const isRunningRef = useRef(false)  // Track if animation loop is active

  // Stable animation function that reads from refs
  const runAnimation = useCallback(() => {
    if (isRunningRef.current) return  // Already running
    isRunningRef.current = true

    const animate = (timestamp) => {
      // Check if skip was requested
      if (skipRequestedRef.current) {
        isRunningRef.current = false
        setIsAnimating(false)
        return
      }

      // Throttle to ~60fps (16ms between frames)
      if (timestamp - lastRenderTimeRef.current < 16) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }
      lastRenderTimeRef.current = timestamp

      const currentIndex = displayIndexRef.current
      const targetText = incomingTextRef.current

      if (currentIndex < targetText.length) {
        setIsAnimating(true)
        let charsToReveal = charsPerFrame

        // Look ahead for word boundaries - reveal complete words when possible
        const remaining = targetText.slice(currentIndex)
        const nextSpace = remaining.indexOf(' ')
        const nextNewline = remaining.indexOf('\n')

        // If we're close to a word boundary, include the whole word
        if (nextSpace > 0 && nextSpace <= charsToReveal + 3) {
          charsToReveal = nextSpace + 1
        } else if (nextNewline > 0 && nextNewline <= charsToReveal + 3) {
          charsToReveal = nextNewline + 1
        }

        // Handle markdown tokens - reveal complete formatting at once
        const nextChar = targetText[currentIndex]
        if (nextChar === '*' || nextChar === '_' || nextChar === '`' || nextChar === '#') {
          const match = remaining.match(/^(\*\*?|__?|`+|#+)([^*_`#\n]*?)(\1)/)
          if (match) {
            charsToReveal = Math.max(charsToReveal, match[0].length)
          }
        }

        displayIndexRef.current = Math.min(currentIndex + charsToReveal, targetText.length)
        setDisplayedText(targetText.slice(0, displayIndexRef.current))

        // Continue animation
        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        // Caught up - but keep checking in case more text arrives
        // Check again after a short delay
        animationFrameRef.current = requestAnimationFrame((ts) => {
          // If more text arrived while we were waiting, continue
          if (displayIndexRef.current < incomingTextRef.current.length) {
            animate(ts)
          } else {
            // Really done
            animationFrameRef.current = null
            isRunningRef.current = false
            setIsAnimating(false)
          }
        })
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate)
  }, [charsPerFrame])

  // Function to skip animation and show full text
  const skipToEnd = useCallback(() => {
    skipRequestedRef.current = true
    isRunningRef.current = false
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    displayIndexRef.current = incomingTextRef.current.length
    setDisplayedText(incomingTextRef.current)
    setIsAnimating(false)
  }, [])

  // When new text arrives, just update the ref and ensure animation is running
  useEffect(() => {
    incomingTextRef.current = incomingText

    // If skip was requested, just update to full text
    if (skipRequestedRef.current) {
      displayIndexRef.current = incomingText.length
      setDisplayedText(incomingText)
      return
    }

    // Start animation if not already running and there's text to reveal
    if (displayIndexRef.current < incomingText.length) {
      runAnimation()
    }

    // No cleanup - don't cancel animation when text updates!
    // The animation reads from refs so it will naturally catch up
  }, [incomingText, runAnimation])

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      isRunningRef.current = false
    }
  }, [])

  // Reset when text resets (new message)
  useEffect(() => {
    if (!incomingText) {
      displayIndexRef.current = 0
      setDisplayedText('')
      setIsAnimating(false)
      skipRequestedRef.current = false
      isRunningRef.current = false
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [incomingText])

  return { displayedText, isAnimating, skipToEnd }
}
